import React, { useState } from 'react';
import { X, Folder, Tag, Loader2, ChevronRight, ChevronDown, Library } from 'lucide-react';
import { CategoryItem, TreeItem } from '../types';
import { apiClient } from '../services/api';
import { trackCardMoved } from '../utils/analytics';
import { useInstance } from '../hooks/useInstance';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

interface MoveToModalProps {
  item: TreeItem;
  categories: CategoryItem[];
  onClose: () => void;
  onMove: (itemId: number, data: { parent_id?: number | null; category_id?: number | null }) => Promise<void>;
}

export const MoveToModal: React.FC<MoveToModalProps> = ({
  item,
  categories,
  onClose,
  onMove,
}) => {
  useBodyScrollLock();
  const { instancesWithRoles, selectedInstanceId } = useInstance();

  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [isMoving, setIsMoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<number>>(new Set());
  const [expandedInstances, setExpandedInstances] = useState<Set<number>>(new Set());
  const [instanceCategories, setInstanceCategories] = useState<Map<number, CategoryItem[]>>(new Map());
  const [loadingInstances, setLoadingInstances] = useState<Set<number>>(new Set());

  const otherInstances = instancesWithRoles.filter(
    (iwr) => iwr.instance.id !== selectedInstanceId && iwr.user_role === 'owner'
  );

  const isSubfolder = (folder: TreeItem): boolean =>
    folder.parent_id !== null && folder.parent_id !== undefined;

  const validateMove = (targetFolderId: number | null): boolean => {
    if (item.is_folder && targetFolderId) {
      const allCategories = [
        ...categories,
        ...Array.from(instanceCategories.values()).flat(),
      ];
      for (const category of allCategories) {
        const findFolder = (items: TreeItem[]): TreeItem | null => {
          for (const it of items) {
            if (it.id === targetFolderId) return it;
            const found = findFolder(it.children);
            if (found) return found;
          }
          return null;
        };
        const target = findFolder(category.children);
        if (target && isSubfolder(target)) {
          setError('Subfolders cannot contain other folders. Please select a top-level folder or category.');
          return false;
        }
      }
    }
    return true;
  };

  const handleMove = async () => {
    setError(null);
    const moveData: { parent_id?: number | null; category_id?: number | null } = {};
    if (selectedCategoryId) {
      moveData.category_id = selectedCategoryId;
      moveData.parent_id = null;
    } else if (selectedFolderId) {
      moveData.parent_id = selectedFolderId;
    } else {
      setError('Please select a destination');
      return;
    }
    if (!validateMove(selectedFolderId)) return;
    try {
      setIsMoving(true);
      await onMove(item.id, moveData);
      trackCardMoved(item.id);
      onClose();
    } catch (err: any) {
      const status = err.response?.status;
      if (status === 403) {
        setError('You need to be the owner of both collections to move items between them.');
      } else {
        setError(err.response?.data?.detail || 'Failed to move item');
      }
    } finally {
      setIsMoving(false);
    }
  };

  const toggleFolder = (folderId: number) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      next.has(folderId) ? next.delete(folderId) : next.add(folderId);
      return next;
    });
  };

  const toggleInstance = async (instanceId: number) => {
    if (expandedInstances.has(instanceId)) {
      setExpandedInstances((prev) => { const next = new Set(prev); next.delete(instanceId); return next; });
      return;
    }
    setExpandedInstances((prev) => { const next = new Set(prev); next.add(instanceId); return next; });
    if (!instanceCategories.has(instanceId)) {
      setLoadingInstances((prev) => { const next = new Set(prev); next.add(instanceId); return next; });
      try {
        const cats = await apiClient.getCategories(instanceId);
        const mapped: CategoryItem[] = cats.map((c) => ({ id: c.id, name: c.name, isExpanded: false, children: [] }));
        setInstanceCategories((prev) => new Map(prev).set(instanceId, mapped));
      } catch {
        setError('Failed to load categories for this collection.');
      } finally {
        setLoadingInstances((prev) => { const next = new Set(prev); next.delete(instanceId); return next; });
      }
    }
  };

  const renderFolder = (folder: TreeItem, level: number = 0): React.ReactNode => {
    const isExpanded = expandedFolders.has(folder.id);
    const isSub = isSubfolder(folder);
    const canSelect = !isSub || !item.is_folder;
    const hasFolderChildren = folder.children.filter((c) => c.is_folder).length > 0;

    return (
      <div key={folder.id} className={level > 0 ? 'ml-4 mt-1' : 'mt-1'}>
        <div
          className={`flex items-center p-2 rounded-md cursor-pointer transition-colors ${
            selectedFolderId === folder.id
              ? 'bg-blue-100 border-2 border-blue-500'
              : canSelect
              ? 'hover:bg-gray-100'
              : 'opacity-50 cursor-not-allowed'
          }`}
          onClick={() => {
            if (canSelect) {
              setSelectedFolderId(folder.id);
              setSelectedCategoryId(null);
              setError(null);
            }
          }}
        >
          {hasFolderChildren ? (
            <button onClick={(e) => { e.stopPropagation(); toggleFolder(folder.id); }} className="mr-2 p-1 hover:bg-gray-200 rounded">
              {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            </button>
          ) : (
            <div className="w-6 mr-2" />
          )}
          <Folder className="h-4 w-4 mr-2 text-blue-600" />
          <span className="text-sm flex-1">{folder.name}</span>
          {isSub && item.is_folder && <span className="text-xs text-gray-400 ml-2">Not allowed</span>}
        </div>
        {isExpanded && hasFolderChildren && (
          <div className="ml-4">
            {folder.children.filter((c) => c.is_folder).map((c) => renderFolder(c, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderCategoryList = (cats: CategoryItem[]) =>
    cats.map((category) => {
      const categoryFolders = category.children.filter((c) => c.is_folder);
      const isCategoryExpanded = expandedFolders.has(category.id);
      return (
        <div key={category.id} className="border border-gray-200 rounded-lg overflow-hidden">
          <div
            className={`flex items-center p-3 cursor-pointer transition-colors ${
              selectedCategoryId === category.id
                ? 'bg-purple-100 border-b border-purple-300'
                : 'hover:bg-gray-50 border-b border-gray-200'
            }`}
            onClick={() => { setSelectedCategoryId(category.id); setSelectedFolderId(null); setError(null); }}
          >
            {categoryFolders.length > 0 ? (
              <button onClick={(e) => { e.stopPropagation(); toggleFolder(category.id); }} className="mr-2 p-1 hover:bg-gray-200 rounded">
                {isCategoryExpanded ? <ChevronDown className="h-4 w-4 text-gray-600" /> : <ChevronRight className="h-4 w-4 text-gray-600" />}
              </button>
            ) : (
              <div className="w-6 mr-2" />
            )}
            <Tag className="h-4 w-4 mr-2 text-purple-600" />
            <span className="text-sm font-semibold flex-1">{category.name}</span>
            {categoryFolders.length > 0 && <span className="text-xs text-gray-400">({categoryFolders.length} folders)</span>}
          </div>
          {isCategoryExpanded && categoryFolders.length > 0 && (
            <div className="bg-gray-50 p-2">
              {categoryFolders.map((f) => renderFolder(f, 0))}
            </div>
          )}
        </div>
      );
    });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">Move To</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5">
          <p className="text-sm text-gray-600">Moving: <span className="font-semibold">{item.name}</span></p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">{error}</div>
          )}

          {/* Current collection */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Current collection</p>
            <div className="space-y-2">{renderCategoryList(categories)}</div>
          </div>

          {/* Other collections */}
          {otherInstances.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Other collections</p>
              <div className="space-y-2">
                {otherInstances.map((iwr) => {
                  const isExpanded = expandedInstances.has(iwr.instance.id);
                  const isLoading = loadingInstances.has(iwr.instance.id);
                  const cats = instanceCategories.get(iwr.instance.id);
                  return (
                    <div key={iwr.instance.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        className="w-full flex items-center p-3 hover:bg-gray-50 transition-colors"
                        onClick={() => toggleInstance(iwr.instance.id)}
                      >
                        {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-500 mr-2" /> : <ChevronRight className="h-4 w-4 text-gray-500 mr-2" />}
                        <Library className="h-4 w-4 mr-2 text-gray-500" />
                        <span className="text-sm font-semibold text-gray-700 flex-1 text-left">{iwr.instance.name}</span>
                        {isLoading && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
                      </button>
                      {isExpanded && !isLoading && cats && (
                        <div className="border-t border-gray-100 p-2 bg-gray-50 space-y-2">
                          {cats.length === 0
                            ? <p className="text-xs text-gray-400 px-2 py-1">No categories in this collection</p>
                            : renderCategoryList(cats)
                          }
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-4 sm:p-6 border-t border-gray-200">
          <button onClick={onClose} disabled={isMoving} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
          <button
            onClick={handleMove}
            disabled={isMoving || (!selectedCategoryId && !selectedFolderId)}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isMoving ? <><Loader2 className="h-4 w-4 animate-spin" />Moving...</> : 'Move'}
          </button>
        </div>
      </div>
    </div>
  );
};
